import React from 'react';
import { Button as ShadcnButton, ButtonProps as ShadcnButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface GlueButtonProps extends Omit<ShadcnButtonProps, 'variant'> {
    baseColor?: string;
    hoverColor?: string;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

export const Button = React.forwardRef<HTMLButtonElement, GlueButtonProps>(
    ({ className, baseColor, hoverColor, variant = 'default', style, ...props }, ref) => {
        // If custom colors are provided, use them via CSS custom properties
        const customStyle = React.useMemo(() => {
            const baseStyle = style || {};
            if (baseColor || hoverColor) {
                return {
                    ...baseStyle,
                    '--button-base-color': baseColor,
                    '--button-hover-color': hoverColor,
                } as React.CSSProperties;
            }
            return baseStyle;
        }, [style, baseColor, hoverColor]);

        const customClassName = cn(
            // Add custom color support if colors are provided
            baseColor && 'bg-[var(--button-base-color)]',
            hoverColor && 'hover:bg-[var(--button-hover-color)]',
            // Maintain Glue's styling characteristics
            'min-w-[120px] rounded-lg transition-colors duration-200',
            className,
        );

        return <ShadcnButton ref={ref} variant={variant} className={customClassName} style={customStyle} {...props} />;
    },
);

Button.displayName = 'Button';
